/**
 * PWA del portal — instalable, sin service worker.
 *
 * Decisión: no hay cache offline a propósito. Un service worker sirviendo el
 * shell viejo después de un deploy es un bug difícil de ver y caro de revertir;
 * el valor real acá es que el miembro tenga el portal en el home del teléfono.
 *
 * Mientras el portal está montado, el documento apunta a `/portal.webmanifest`
 * (start_url `/portal`, mismo scope `/` que el sitio para que la navegación
 * interna no salga de la app). Al desmontar se restaura el manifest del sitio.
 */

import { useCallback, useEffect, useState } from "react";

const PORTAL_MANIFEST = "/portal.webmanifest";

/** Evento no estándar de Chromium; no está en lib.dom. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function isStandaloneDisplay(): boolean {
  if (typeof window === "undefined") return false;
  const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia("(display-mode: standalone)").matches;
}

function isIosSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const ios = /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  return ios && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

export interface PortalPwa {
  /** Chromium expuso el prompt nativo: se puede instalar con un tap. */
  canInstall: boolean;
  /** Ya corre instalada: no ofrecer instalación. */
  isStandalone: boolean;
  /** iOS no expone prompt: hay que explicar Compartir → Agregar a inicio. */
  needsIosHint: boolean;
  install: () => Promise<"accepted" | "dismissed" | "unavailable">;
}

export function usePortalPwa(): PortalPwa {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(isStandaloneDisplay);

  // Apuntar el documento al manifest del portal mientras esté montado.
  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (!link) return;
    const previous = link.getAttribute("href");
    link.setAttribute("href", PORTAL_MANIFEST);
    return () => {
      if (previous) link.setAttribute("href", previous);
    };
  }, []);

  useEffect(() => {
    const onPrompt = (event: Event) => {
      event.preventDefault(); // sin esto Chrome muestra su propio banner
      setDeferred(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setDeferred(null);
      setIsStandalone(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return "unavailable" as const;
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setDeferred(null); // el evento es de un solo uso
    return outcome;
  }, [deferred]);

  return {
    canInstall: Boolean(deferred) && !isStandalone,
    isStandalone,
    needsIosHint: isIosSafari() && !isStandalone && !deferred,
    install,
  };
}
